import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Search } from "lucide-react";
import {
  businessesApi,
  entitiesApi,
  personsApi,
} from "@/api/counterparties";
import { Badge, Button, FormField } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/design-system/lib/utils";
import type {
  BusinessDedupCheckRequest,
  CounterpartyKind,
  DedupCandidateDto,
  DedupCheckResultDto,
  DedupMatchStrength,
  EntityDedupCheckRequest,
  PersonDedupCheckRequest,
} from "@/types/counterparties";

// Deal Pipeline spec §5.7 / §6.3 — reusable dedup-check modal. Consumed by
// the standalone counterparty Create flow and by the New Deal wizard's
// Business lookup (DealBusinessLookup).

type DedupRequestForKind = {
  Person: PersonDedupCheckRequest;
  Entity: EntityDedupCheckRequest;
  Business: BusinessDedupCheckRequest;
};

interface DedupCheckModalProps<K extends CounterpartyKind> {
  open: boolean;
  kind: K;
  request: DedupRequestForKind[K];
  /** Pre-computed result, if the parent already ran the check (useful in
   *  tests and when the parent wants to control the network call). When
   *  omitted, the modal runs the check itself when it opens. */
  result?: DedupCheckResultDto;
  /** Called when the user picks an existing candidate. */
  onLinkExisting: (candidate: DedupCandidateDto) => void;
  /** Called when the user opts to continue creating a new record. The
   *  reason is required for Strong matches and optional for everything
   *  else; an empty string is passed when no reason was captured. */
  onCreateNew: (reason: string) => void;
  onCancel: () => void;
}

const STRENGTH_BADGE: Record<DedupMatchStrength, "error" | "review" | "info" | "finalized"> = {
  Strong: "error",
  Likely: "review",
  Possible: "info",
  None: "finalized",
};

const STRENGTH_LABEL: Record<DedupMatchStrength, string> = {
  Strong: "Strong match",
  Likely: "Likely match",
  Possible: "Possible match",
  None: "No match",
};

export function DedupCheckModal<K extends CounterpartyKind>({
  open,
  kind,
  request,
  result: presetResult,
  onLinkExisting,
  onCreateNew,
  onCancel,
}: DedupCheckModalProps<K>) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [autoResult, setAutoResult] = useState<DedupCheckResultDto | null>(null);

  const dedupMutation = useMutation({
    mutationFn: async (): Promise<DedupCheckResultDto> => {
      if (kind === "Person") {
        return personsApi.dedupCheck(request as PersonDedupCheckRequest);
      }
      if (kind === "Entity") {
        return entitiesApi.dedupCheck(request as EntityDedupCheckRequest);
      }
      return businessesApi.dedupCheck(request as BusinessDedupCheckRequest);
    },
    onSuccess: (r) => setAutoResult(r),
  });

  useEffect(() => {
    if (!open) {
      setReason("");
      setReasonError(null);
      setAutoResult(null);
      return;
    }
    if (presetResult) return;
    dedupMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetResult]);

  const result = presetResult ?? autoResult;
  const strength: DedupMatchStrength = result?.strength ?? "None";
  const candidates = result?.candidates ?? [];
  const requireReason = strength === "Strong";

  const handleCreateNew = () => {
    if (requireReason && reason.trim().length === 0) {
      setReasonError(
        "A reason is required to override a Strong match. Explain why this is a different record."
      );
      return;
    }
    onCreateNew(reason.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      dismissOnBackdropClick={false}
      title="Duplicate Check"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant={requireReason ? "destructive" : "primary"}
            onClick={handleCreateNew}
            disabled={dedupMutation.isPending}
          >
            {requireReason ? "Create new anyway" : "Continue creating new"}
          </Button>
        </>
      }
    >
      {dedupMutation.isPending && !result ? (
        <div className="flex items-center gap-space-2 text-small text-gray-mid">
          <Search size={16} strokeWidth={1.5} aria-hidden /> Checking for duplicates…
        </div>
      ) : (
        <div className="flex flex-col gap-space-4">
          <DedupHeader strength={strength} />

          {candidates.length > 0 && (
            <ul className="flex flex-col divide-y divide-border-light border border-border-light rounded-md">
              {candidates.map((c) => (
                <li
                  key={c.id}
                  className={cn(
                    "flex items-center justify-between gap-space-3 p-space-3",
                    c.matchStrength === "Strong" && "bg-status-error/5"
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-space-2">
                      <Badge status={STRENGTH_BADGE[c.matchStrength]}>
                        {STRENGTH_LABEL[c.matchStrength]}
                      </Badge>
                      <span className="font-semibold text-small text-onyx truncate">
                        {c.displayName}
                      </span>
                    </div>
                    <p className="text-caption text-gray-mid mt-space-1">{c.matchReason}</p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onLinkExisting(c)}
                  >
                    Use Existing
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {requireReason && (
            <FormField
              label="Reason for creating a new record"
              required
              error={reasonError ?? undefined}
              helperText="Saved to the audit log per spec §6.3."
            >
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (reasonError) setReasonError(null);
                }}
                placeholder="e.g. Different person — same name but DOB and address don't match."
              />
            </FormField>
          )}
        </div>
      )}
    </Modal>
  );
}

function DedupHeader({ strength }: { strength: DedupMatchStrength }) {
  if (strength === "None") {
    return (
      <div className="flex items-center gap-space-2 text-small text-status-success">
        <CheckCircle2 size={16} strokeWidth={1.5} aria-hidden />
        No matching records found. Safe to continue.
      </div>
    );
  }
  if (strength === "Strong") {
    return (
      <div className="flex items-start gap-space-2 text-small text-status-error">
        <AlertTriangle size={16} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
        <div>
          <p className="font-semibold m-0">Strong match found.</p>
          <p className="m-0 text-gray-dark">
            Phoenix recommends linking to the existing record. To create a new record, you must
            explain why below.
          </p>
        </div>
      </div>
    );
  }
  // Likely / Possible matches: the candidate list speaks for itself, so no
  // explanatory header is shown.
  return null;
}
